import { api } from '@/lib/api'
import type {
  SubscriptionPlan,
  CompanySubscription,
  CompanyInvoice,
  MasterBillingMetrics,
  MasterBillingCompanyRow,
} from '@/types/billing.types'

interface MrrDashboardResponse extends MasterBillingMetrics {
  reference_month: string
  total_active: number
  total_past_due: number
  generated_at: string
}

interface CompaniesOverviewParams {
  page?: number
  limit?: number
  search?: string
  reference_month?: string
}

interface CompaniesOverviewResponse {
  data: MasterBillingCompanyRow[]
  total: number
  page: number
  limit: number
}

interface InvoiceLinkResponse {
  payment_url: string | null
  bank_slip_url: string | null
  due_date: string | null
  amount: number | null
  status: string | null
}

interface SyncAsaasResponse {
  success: boolean
  previous_status: string
  current_status: string
  asaas_status: string
  synced_at: string
}

export const billingService = {
  // Admin: Planos globais
  async getPlans(): Promise<SubscriptionPlan[]> {
    return api.get<SubscriptionPlan[]>('/admin/plans')
  },

  async getActivePlans(): Promise<SubscriptionPlan[]> {
    return api.get<SubscriptionPlan[]>('/admin/plans/active')
  },

  async getPublicPlansBySlug(slug: string): Promise<SubscriptionPlan[]> {
    return api.get<SubscriptionPlan[]>(`/public/plans/by-slug/${slug}`)
  },

  // Planos disponíveis contextualmente por empresa (Whitelabel pai ou Fallback Global)
  async getAvailablePlans(companyId: string): Promise<SubscriptionPlan[]> {
    return api.get<SubscriptionPlan[]>(`/companies/${companyId}/billing/plans`)
  },

  // Tenant / Master: Gestão de Planos Comerciais
  async getCompanyPlans(companyId: string): Promise<SubscriptionPlan[]> {
    return api.get<SubscriptionPlan[]>(`/companies/${companyId}/plans`)
  },

  async getCompanyPlanById(companyId: string, planId: string): Promise<SubscriptionPlan> {
    return api.get<SubscriptionPlan>(`/companies/${companyId}/plans/${planId}`)
  },

  async createCompanyPlan(companyId: string, plan: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> {
    return api.post<SubscriptionPlan>(`/companies/${companyId}/plans`, plan)
  },

  async updateCompanyPlan(companyId: string, planId: string, plan: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> {
    return api.put<SubscriptionPlan>(`/companies/${companyId}/plans/${planId}`, plan)
  },

  async deleteCompanyPlan(companyId: string, planId: string): Promise<{ message: string; id: string }> {
    return api.delete<{ message: string; id: string }>(`/companies/${companyId}/plans/${planId}`)
  },

  // Admin: Gestão de assinatura da empresa específica
  async adminListCompanySubscriptions(companyId: string): Promise<CompanySubscription[]> {
    return api.get<CompanySubscription[]>(`/admin/companies/${companyId}/subscriptions`)
  },

  async adminAssignPlanToCompany(
    companyId: string,
    planId: string,
    spendingLimit: number = 0,
    requirePaymentGateway: boolean = true,
    expiresAt: string | null = null,
    billingCycle: string = 'monthly'
  ): Promise<CompanySubscription> {
    return api.post<CompanySubscription>(`/admin/companies/${companyId}/subscriptions`, {
      plan_id: planId,
      spending_limit: spendingLimit,
      require_payment: requirePaymentGateway,
      end_date: expiresAt,
      billing_cycle: billingCycle,
    })
  },

  async adminCancelCompanySubscription(companyId: string): Promise<void> {
    await api.put(`/admin/companies/${companyId}/subscriptions/active/cancel`, {})
  },

  // Admin: MRR Dashboard
  async getMrrDashboard(referenceMonth?: string): Promise<MrrDashboardResponse> {
    const params = referenceMonth ? `?reference_month=${referenceMonth}` : ''
    return api.get<MrrDashboardResponse>(`/admin/billing/mrr-dashboard${params}`)
  },

  // Admin: Companies Overview com paginação e busca
  async getCompaniesOverview(params: CompaniesOverviewParams = {}): Promise<CompaniesOverviewResponse> {
    const query = new URLSearchParams()
    if (params.page !== undefined) query.set('page', String(params.page))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.search) query.set('search', params.search)
    if (params.reference_month) query.set('reference_month', params.reference_month)
    const qs = query.toString() ? `?${query.toString()}` : ''
    return api.get<CompaniesOverviewResponse>(`/admin/billing/companies-overview${qs}`)
  },

  // Admin: Sincronizar assinatura com Asaas
  async syncAsaas(companyId: string): Promise<SyncAsaasResponse> {
    return api.patch<SyncAsaasResponse>(`/admin/companies/${companyId}/subscriptions/active/sync-asaas`, {})
  },

  // Client
  async updateSpendingLimit(companyId: string, limit: number): Promise<void> {
    await api.put(`/companies/${companyId}/billing/subscription/spending-limit`, { spending_limit: limit })
  },

  async getInvoices(companyId: string): Promise<CompanyInvoice[]> {
    return api.get<CompanyInvoice[]>(`/companies/${companyId}/billing/invoices`)
  },

  async getInvoiceLink(companyId: string): Promise<InvoiceLinkResponse> {
    return api.get<InvoiceLinkResponse>(`/companies/${companyId}/billing/invoice-link`)
  },

  async initiateCheckout(payload: {
    user: any;
    company: any;
    subscription: {
      plan_id: string;
      billing_type: 'PIX' | 'CREDIT_CARD' | 'BOLETO';
      cycle: string;
    }
  }): Promise<{
    payment_url?: string;
    invoice_url?: string;
    pix?: any;
    boleto?: any;
    [key: string]: any;
  }> {
    return api.post('/public/checkout/initiate', payload);
  },

  async subscribeToPlan(companyId: string, planId: string, billingType?: string, billingCycle: string = 'monthly'): Promise<any> {
    return api.post(`/companies/${companyId}/billing/subscription/subscribe`, {
      plan_id: planId,
      billing_type: billingType,
      billing_cycle: billingCycle,
      cycle: billingCycle === 'annual' || billingCycle === 'yearly' ? 'YEARLY' : 'MONTHLY'
    });
  },

  async getPendingBilling(companyId: string): Promise<{
    has_pending_payment: boolean;
    pending_details?: {
      subscription_id: string;
      invoice_id: string;
      status: string;
      plan_name: string;
      amount: number;
      billing_type: string;
      payment_url?: string;
      invoice_url?: string;
      pix?: any;
    };
  }> {
    return api.get(`/companies/${companyId}/billing/pending`);
  }
}

